import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Search, Plus, Trash2, Film, Tv, Star, Clock, Bookmark, Gamepad2, Lock, ChevronDown, Check, ZoomIn } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config/api';
import { searchMulti, getImageUrl } from '../services/tmdb';

/* ─── Countdown Hook ─── */
const useCountdown = (targetDateStr) => {
  const [timeLeft, setTimeLeft] = useState('');
  useEffect(() => {
    if (!targetDateStr) { setTimeLeft(''); return; }
    const tick = () => {
      const diff = new Date(targetDateStr).getTime() - Date.now();
      if (diff <= 0) { setTimeLeft('Airing now'); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(h > 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h}h ${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDateStr]);
  return timeLeft;
};

/* ─── Episode Card ─── */
const EpisodeCard = ({ epData, onPlay }) => {
  const countdown = useCountdown(epData?.air_datetime);
  if (!epData?.found) return null;
  const airDate = epData.air_datetime ? new Date(epData.air_datetime) : null;
  const isPast = epData.state === 'COMPLETE' || epData.state === 'POST';
  return (
    <div className="mt-1.5 bg-zinc-700/40 rounded-lg p-2.5" data-testid={`episode-card-${epData.show_name}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-purple-400 font-semibold truncate">
            S{epData.season_number} E{String(epData.episode_number).padStart(2, '0')}
            {epData.episode_title ? ` – ${epData.episode_title}` : ''}
          </p>
          {airDate && (
            <p className="text-[10px] text-gray-500 mt-0.5">
              {airDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })},{' '}
              {airDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </p>
          )}
        </div>
        {countdown && !isPast && (
          <div className="flex items-center gap-1 bg-zinc-800 rounded-full px-2 py-0.5 flex-shrink-0">
            <Clock className="h-3 w-3 text-amber-400" />
            <span className="text-[10px] text-amber-400 font-mono font-bold" data-testid="episode-countdown">{countdown}</span>
          </div>
        )}
      </div>
      {!isPast && onPlay && (
        <button
          onClick={onPlay}
          className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold transition-colors"
          data-testid={`play-btn-${epData.show_name}`}
        >
          <Gamepad2 className="h-3.5 w-3.5" /> Play
        </button>
      )}
    </div>
  );
};

/* ─── Locked Card ─── */
const LockedCard = ({ prediction, epData }) => {
  const countdown = useCountdown(epData?.air_datetime);
  if (!epData?.found) return null;
  const airDate = epData.air_datetime ? new Date(epData.air_datetime) : null;
  return (
    <div className="mt-1.5 bg-zinc-700/40 rounded-lg p-2.5" data-testid={`locked-card-${epData.show_name}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] text-purple-400 font-semibold truncate">
            S{epData.season_number} E{String(epData.episode_number).padStart(2, '0')}
            {epData.episode_title ? ` – ${epData.episode_title}` : ''}
          </p>
          {airDate && (
            <p className="text-[10px] text-gray-500 mt-0.5">
              {airDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })},{' '}
              {airDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </p>
          )}
        </div>
        {countdown && (
          <div className="flex items-center gap-1 bg-zinc-800 rounded-full px-2 py-0.5 flex-shrink-0">
            <Clock className="h-3 w-3 text-amber-400" />
            <span className="text-[10px] text-amber-400 font-mono font-bold">{countdown}</span>
          </div>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2 py-1.5 px-2.5 rounded-md bg-zinc-800/80 border border-zinc-600">
        <Lock className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-gray-400">Prediction Submitted</p>
          <p className="text-xs text-white font-medium truncate">{prediction.contestant_name}</p>
        </div>
        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
      </div>
    </div>
  );
};

/* ─── Prediction Modal ─── */
const PredictionModal = ({ isOpen, onClose, showName, epData, userId, onSubmitted }) => {
  const [cast, setCast] = useState([]);
  const [loadingCast, setLoadingCast] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    if (isOpen && showName) {
      setSelected(null);
      setDropdownOpen(false);
      setLoadingCast(true);
      const params = { show_name: showName };
      if (epData?.season_number) params.season_number = epData.season_number;
      if (epData?.episode_number) params.episode_number = epData.episode_number;
      axios.get(`${API_URL}/api/watchlist/cast`, { params })
        .then(res => {
          const data = res.data;
          setCast(data.cast || data || []);
        })
        .catch(() => setCast([]))
        .finally(() => setLoadingCast(false));
    }
  }, [isOpen, showName, epData]);

  const handleSubmit = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/watchlist/predict`, null, {
        params: {
          user_id: userId,
          show_name: showName,
          contestant_name: selected.name,
          season_number: epData?.season_number || 0,
          episode_number: epData?.episode_number || 0,
          episode_title: epData?.episode_title || '',
          air_datetime: epData?.air_datetime || '',
        },
      });
      toast.success('Prediction submitted!', { description: `You picked: ${selected.name}` });
      onSubmitted({ contestant_name: selected.name });
      onClose();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to submit';
      toast.error(msg);
    }
    setSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm bg-zinc-900 text-white border-zinc-700 p-0 max-h-[85vh] overflow-hidden">
        <DialogTitle className="sr-only">Elimination Prediction</DialogTitle>
        <DialogDescription className="sr-only">Pick who will be eliminated</DialogDescription>

        <div className="px-4 pt-4 pb-3 border-b border-zinc-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-purple-600/20 flex items-center justify-center">
              <Gamepad2 className="h-4 w-4 text-purple-400" />
            </div>
            <div>
              <h3 className="text-sm font-bold leading-tight" data-testid="prediction-modal-title">Who will be eliminated tonight?</h3>
              <p className="text-[10px] text-gray-500">{showName}</p>
            </div>
          </div>
        </div>

        <div className="px-4 py-4 space-y-4">
          {loadingCast ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-5 w-5 border-2 border-purple-500 border-t-transparent rounded-full" />
            </div>
          ) : cast.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-gray-400 text-sm">No cast data available for this show.</p>
            </div>
          ) : (
            <>
              {/* Dropdown selector */}
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-zinc-800 border border-zinc-600 rounded-lg text-sm hover:border-zinc-500 transition-colors"
                  data-testid="contestant-dropdown"
                >
                  {selected ? (
                    <div className="flex items-center gap-2">
                      {selected.image && (
                        <img src={selected.image} alt="" className="w-6 h-6 rounded-full object-cover" />
                      )}
                      <span className="text-white">{selected.name}</span>
                    </div>
                  ) : (
                    <span className="text-gray-500">Select a contestant...</span>
                  )}
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-800 border border-zinc-600 rounded-lg max-h-48 overflow-y-auto z-20 shadow-xl" data-testid="contestant-list">
                    {cast.map((person) => (
                      <button
                        key={person.id || person.name}
                        onClick={() => { setSelected(person); setDropdownOpen(false); }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-zinc-700 transition-colors text-sm ${
                          selected?.name === person.name ? 'bg-purple-600/20 text-purple-300' : 'text-white'
                        }`}
                        data-testid={`contestant-option-${person.name}`}
                      >
                        {person.image ? (
                          <img src={person.image} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-zinc-600 flex items-center justify-center flex-shrink-0 text-xs text-gray-400">
                            {person.name[0]}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="truncate font-medium">{person.name}</p>
                          {person.character && <p className="text-[10px] text-gray-500 truncate">as {person.character}</p>}
                        </div>
                        {selected?.name === person.name && <Check className="h-4 w-4 text-purple-400 ml-auto flex-shrink-0" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="text-center text-[10px] text-gray-600">
                +5 points for correct prediction
              </div>

              <Button
                onClick={handleSubmit}
                disabled={!selected || submitting}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-zinc-700 disabled:text-gray-500 text-white font-semibold h-10"
                data-testid="submit-prediction-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Prediction'}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN WATCHLIST COMPONENT
   ═══════════════════════════════════════════════════════ */
const Watchlist = ({ user, isOpen, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const searchTimeout = useRef(null);

  // Episode + prediction state keyed by item title
  const [episodeData, setEpisodeData] = useState({});
  const [predictions, setPredictions] = useState({});
  const [predictionModal, setPredictionModal] = useState(null); // { showName, epData }

  const fetchWatchlist = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/watchlist/${user.id}`);
      setItems(res.data || []);
    } catch (e) { console.error('Fetch watchlist error:', e); }
    finally { setLoading(false); }
  }, [user.id]);

  useEffect(() => {
    if (isOpen) fetchWatchlist();
  }, [isOpen, fetchWatchlist]);

  // Fetch next episode + prediction status for each TV series
  useEffect(() => {
    if (!isOpen || items.length === 0) return;
    const tvItems = items.filter(i => i.media_type === 'tv');
    tvItems.forEach(async (item) => {
      const showName = item.title;
      try {
        const epRes = await axios.get(`${API_URL}/api/reality-hub/next-episode`, { params: { show_name: showName } });
        setEpisodeData(prev => ({ ...prev, [showName]: epRes.data }));

        if (epRes.data?.found) {
          const predRes = await axios.get(`${API_URL}/api/watchlist/prediction`, {
            params: {
              user_id: user.id,
              show_name: showName,
              season_number: epRes.data.season_number || 0,
              episode_number: epRes.data.episode_number || 0,
            },
          });
          if (predRes.data?.has_prediction) {
            setPredictions(prev => ({ ...prev, [showName]: predRes.data.prediction }));
          }
        }
      } catch { /* silently skip */ }
    });
  }, [isOpen, items, user.id]);

  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const results = await searchMulti(query);
      setSearchResults(results.filter(r => r.media_type === 'movie' || r.media_type === 'tv').slice(0, 8));
    } catch (e) { console.error('Search error:', e); }
    setSearching(false);
  }, []);

  const onSearchInput = (value) => {
    setSearchQuery(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => handleSearch(value), 400);
  };

  const addItem = async (item) => {
    try {
      await axios.post(`${API_URL}/api/watchlist?user_id=${user.id}&tmdb_id=${item.id}&media_type=${item.media_type}&title=${encodeURIComponent(item.title || item.name)}&poster_path=${encodeURIComponent(item.poster_path || '')}&overview=${encodeURIComponent((item.overview || '').substring(0, 200))}&vote_average=${item.vote_average || 0}`);
      setSearchQuery('');
      setSearchResults([]);
      setShowSearch(false);
      fetchWatchlist();
    } catch (e) {
      if (e.response?.status === 400) setSearchResults(prev => prev.filter(r => r.id !== item.id));
    }
  };

  const removeItem = async (tmdbId) => {
    try {
      await axios.delete(`${API_URL}/api/watchlist/${user.id}/${tmdbId}`);
      setItems(prev => prev.filter(i => i.tmdb_id !== tmdbId));
    } catch (e) { console.error('Remove error:', e); }
  };

  const handlePredictionSubmitted = (showName, pred) => {
    setPredictions(prev => ({ ...prev, [showName]: pred }));
  };

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-lg bg-zinc-900 text-white border-zinc-700 max-h-[90vh] overflow-y-auto p-0">
          <DialogTitle className="sr-only">Watchlist</DialogTitle>
          <DialogDescription className="sr-only">Manage your watchlist</DialogDescription>

          <div className="sticky top-0 bg-zinc-900 z-10 px-4 pt-4 pb-3 border-b border-zinc-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bookmark className="h-5 w-5 text-amber-400" />
                <h2 className="text-base font-bold">Watchlist</h2>
                <span className="text-xs text-gray-500">({items.length})</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowSearch(!showSearch)}
                  className={`p-1.5 rounded-lg transition-colors ${showSearch ? 'bg-red-600/20 text-red-400' : 'hover:bg-zinc-700 text-gray-400'}`}
                  data-testid="watchlist-add-btn"
                >
                  <Plus className="h-4 w-4" />
                </button>
                <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="watchlist-close-btn">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {showSearch && (
              <div className="mt-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => onSearchInput(e.target.value)}
                    placeholder="Search movies or shows to add..."
                    className="bg-zinc-800 border-zinc-700 text-white h-10 pl-10 text-sm"
                    autoFocus
                    data-testid="watchlist-search-input"
                  />
                </div>
                {searching && (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full" />
                  </div>
                )}
                {searchResults.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-60 overflow-y-auto">
                    {searchResults.map(item => {
                      const alreadyAdded = items.some(i => i.tmdb_id === item.id);
                      return (
                        <button
                          key={`${item.media_type}-${item.id}`}
                          onClick={() => !alreadyAdded && addItem(item)}
                          disabled={alreadyAdded}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${alreadyAdded ? 'opacity-40 cursor-not-allowed' : 'hover:bg-zinc-800'}`}
                          data-testid={`watchlist-search-result-${item.id}`}
                        >
                          <div className="w-8 h-12 bg-zinc-700 rounded overflow-hidden flex-shrink-0">
                            {item.poster_path ? (
                              <img src={getImageUrl(item.poster_path, 'w92')} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-zinc-500"><Film className="h-3 w-3" /></div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-sm font-medium truncate">{item.title || item.name}</p>
                            <p className="text-gray-500 text-xs">
                              {(item.release_date || item.first_air_date || '').substring(0, 4)}
                              {' '}&middot;{' '}{item.media_type === 'movie' ? 'Movie' : 'TV'}
                            </p>
                          </div>
                          {alreadyAdded ? (
                            <span className="text-xs text-green-500">Added</span>
                          ) : (
                            <Plus className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-4 py-3">
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin h-6 w-6 border-2 border-amber-500 border-t-transparent rounded-full" />
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-10">
                <Bookmark className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">Your watchlist is empty</p>
                <p className="text-gray-600 text-xs mt-1">Search for movies and shows to add</p>
                <Button
                  onClick={() => setShowSearch(true)}
                  className="mt-3 bg-red-600 hover:bg-red-700 text-white text-sm h-9"
                  data-testid="watchlist-empty-add-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />Add Content
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {items.map(item => {
                  const epData = item.media_type === 'tv' ? episodeData[item.title] : null;
                  const pred = item.media_type === 'tv' ? predictions[item.title] : null;
                  return (
                    <div
                      key={item.id || item.tmdb_id}
                      className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-750 group"
                      data-testid={`watchlist-item-${item.tmdb_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-14 bg-zinc-700 rounded overflow-hidden flex-shrink-0">
                          {item.poster_path ? (
                            <img src={getImageUrl(item.poster_path, 'w92')} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-zinc-500">
                              {item.media_type === 'tv' ? <Tv className="h-4 w-4" /> : <Film className="h-4 w-4" />}
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-white text-sm font-medium truncate">{item.title}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="capitalize">{item.media_type}</span>
                            {item.vote_average > 0 && (
                              <span className="flex items-center gap-0.5"><Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />{item.vote_average.toFixed(1)}</span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => removeItem(item.tmdb_id)}
                          className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-zinc-700 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                          data-testid={`watchlist-remove-${item.tmdb_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Next Episode + Play / Locked */}
                      {item.media_type === 'tv' && epData && pred ? (
                        <LockedCard prediction={pred} epData={epData} />
                      ) : item.media_type === 'tv' && epData?.found ? (
                        <EpisodeCard
                          epData={epData}
                          onPlay={() => setPredictionModal({ showName: item.title, epData })}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Prediction Modal */}
      <PredictionModal
        isOpen={!!predictionModal}
        onClose={() => setPredictionModal(null)}
        showName={predictionModal?.showName || ''}
        epData={predictionModal?.epData}
        userId={user.id}
        onSubmitted={(pred) => {
          if (predictionModal?.showName) handlePredictionSubmitted(predictionModal.showName, pred);
        }}
      />
    </>
  );
};

export default Watchlist;
